import { useState } from 'react';
import axios from 'axios';
import { Upload, FileText, Plus, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataCapture = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    field: '',
    value: '',
    unit: ''
  });
  const [submittedData, setSubmittedData] = useState([]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExtractedData(response.data);
      toast.success(`Document processed: ${response.data.document_type}`, {
        description: 'Data extracted successfully'
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to process document');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API}/form-data`, {
        category: formData.category,
        field: formData.field,
        value: parseFloat(formData.value),
        unit: formData.unit
      });
      
      setSubmittedData([...submittedData, response.data]);
      toast.success('Data saved successfully', {
        description: `Emissions calculated: ${response.data.emissions} kg CO₂e`
      });
      
      setFormData({ category: '', field: '', value: '', unit: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save data');
    }
  };

  const quickFormTemplates = [
    { category: 'Electricity', field: 'Monthly Consumption', unit: 'kWh', placeholder: 'e.g., 8500' },
    { category: 'Diesel', field: 'Monthly Usage', unit: 'Liters', placeholder: 'e.g., 1200' },
    { category: 'Water', field: 'Monthly Consumption', unit: 'Liters', placeholder: 'e.g., 35000' },
    { category: 'Waste', field: 'Monthly Generation', unit: 'kg', placeholder: 'e.g., 450' }
  ];

  return (
    <div className="p-8" data-testid="data-capture-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          ESG Data Capture
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Upload documents or input data manually to build your ESG baseline
        </p>
      </div>

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-8" data-testid="data-capture-tabs">
          <TabsTrigger value="upload" data-testid="upload-tab">Document Upload</TabsTrigger>
          <TabsTrigger value="form" data-testid="form-tab">Manual Entry</TabsTrigger>
        </TabsList>

        {/* Document Upload Tab */}
        <TabsContent value="upload" className="space-y-6">
          <Card className="border-[#E5E1D8]">
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Upload ESG Documents</CardTitle>
              <CardDescription>Upload electricity bills, fuel invoices, water bills, or policy documents</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-[#E5E1D8] rounded-xl p-12 text-center hover:border-[#1A362D] transition-all duration-200">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer" data-testid="file-upload-label">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-[#1A362D]/10 rounded-xl flex items-center justify-center">
                      <Upload className="w-8 h-8 text-[#1A362D]" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#111814] mb-1">Click to upload or drag and drop</p>
                      <p className="text-sm text-[#6B7280]">PDF, JPG, PNG up to 10MB</p>
                    </div>
                    <Button type="button" className="bg-[#1A362D] hover:bg-[#2D4A3E]" data-testid="upload-button">
                      Select File
                    </Button>
                  </div>
                </label>
              </div>

              {uploadedFile && (
                <div className="mt-6 p-4 bg-[#2D4A3E]/10 rounded-lg border border-[#2D4A3E]/30" data-testid="uploaded-file-info">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2D4A3E]" />
                    <div>
                      <p className="font-semibold text-[#111814]">{uploadedFile.name}</p>
                      <p className="text-sm text-[#6B7280]">{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                </div>
              )}

              {extractedData && (
                <div className="mt-6" data-testid="extracted-data">
                  <h3 className="text-lg font-semibold text-[#111814] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Extracted Data (AI Simulation)
                  </h3>
                  <div className="bg-white p-4 rounded-lg border border-[#E5E1D8] space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[#6B7280]">Document Type:</span>
                      <span className="text-sm font-semibold text-[#111814]">{extractedData.document_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[#6B7280]">Scope:</span>
                      <span className="text-sm font-semibold text-[#111814]">{extractedData.scope}</span>
                    </div>
                    <div className="border-t border-[#E5E1D8] pt-2 mt-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-2">Extracted Values</p>
                      {Object.entries(extractedData.extracted_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-1">
                          <span className="text-sm text-[#4B5563]">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-sm font-medium text-[#111814]">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Entry Tab */}
        <TabsContent value="form" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Entry Forms */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Quick Entry
              </h3>
              {quickFormTemplates.map((template, index) => (
                <Card key={index} className="border-[#E5E1D8]">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{template.category}</CardTitle>
                    <CardDescription>{template.field}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const value = e.target.value.value;
                        if (value) {
                          axios.post(`${API}/form-data`, {
                            category: template.category,
                            field: template.field,
                            value: parseFloat(value),
                            unit: template.unit
                          }).then(response => {
                            setSubmittedData([...submittedData, response.data]);
                            toast.success('Data saved');
                            e.target.reset();
                          });
                        }
                      }}
                      className="flex gap-2"
                    >
                      <Input
                        name="value"
                        type="number"
                        placeholder={template.placeholder}
                        className="border-[#E5E1D8]" 
                        data-testid={`quick-input-${template.category.toLowerCase()}`}
                      />
                      <span className="flex items-center text-sm text-[#6B7280] whitespace-nowrap px-3">{template.unit}</span>
                      <Button type="submit" size="sm" className="bg-[#1A362D] hover:bg-[#2D4A3E]" data-testid={`quick-submit-${template.category.toLowerCase()}`}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Custom Entry Form */}
            <div>
              <h3 className="text-xl font-semibold text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Custom Entry
              </h3>
              <Card className="border-[#E5E1D8]">
                <CardHeader>
                  <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Add Custom Data</CardTitle>
                  <CardDescription>Enter any ESG-related metric</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="custom-form">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g., Natural Gas, Travel, Procurement"
                        className="border-[#E5E1D8]"
                        required
                        data-testid="custom-category-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="field">Field Name</Label>
                      <Input
                        id="field"
                        value={formData.field}
                        onChange={(e) => setFormData({ ...formData, field: e.target.value })}
                        placeholder="e.g., Monthly Usage"
                        className="border-[#E5E1D8]"
                        required
                        data-testid="custom-field-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="value">Value</Label>
                        <Input
                          id="value"
                          type="number"
                          step="0.01"
                          value={formData.value}
                          onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                          placeholder="0.00"
                          className="border-[#E5E1D8]"
                          required
                          data-testid="custom-value-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="unit">Unit</Label>
                        <Input
                          id="unit"
                          value={formData.unit}
                          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                          placeholder="e.g., kWh, kg, L"
                          className="border-[#E5E1D8]"
                          required
                          data-testid="custom-unit-input"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#1A362D] hover:bg-[#2D4A3E]" data-testid="custom-submit-button">
                      Save Data
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Submitted Data */}
              {submittedData.length > 0 && (
                <Card className="mt-6 border-[#E5E1D8]" data-testid="submitted-data-list">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Recent Submissions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {submittedData.slice(-5).reverse().map((data, index) => (
                        <div key={index} className="p-3 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-[#111814]">{data.category}</p>
                              <p className="text-sm text-[#6B7280]">{data.field}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-[#111814]">{data.value} {data.unit}</p>
                              <p className="text-xs text-[#C85A32]">{data.emissions} kg CO₂e</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataCapture;
